import { useState, useRef, useCallback } from 'react';

interface MediaRecordingOptions {
  onTranscriptReady?: (transcript: string) => void;
}

export const useMediaRecording = (onTranscriptUpdate?: (text: string) => void) => {
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [audioTranscript, setAudioTranscript] = useState<string>('');
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, []);

  const startRecording = useCallback(async () => {
    try {
      chunksRef.current = [];
      let mediaStream;

      // For iOS, we need to request permissions separately and explicitly
      if (isIOS) {
        // First request audio
        await navigator.mediaDevices.getUserMedia({ audio: true });
        // Then request video
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: {
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        });
        mediaStream = stream;
      } else {
        // For non-iOS devices, request both simultaneously
        mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
          },
          video: {
            facingMode: 'user',
            width: { ideal: 1280 },
            height: { ideal: 720 }
          }
        });
      }

      setStream(mediaStream);

      // Determine supported mime type
      const mimeType = isIOS ? 'video/mp4' : 'video/webm';
      const options = {
        mimeType: MediaRecorder.isTypeSupported(mimeType) ? mimeType : 'video/webm',
        videoBitsPerSecond: 2500000
      };

      const recorder = new MediaRecorder(mediaStream, options);

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        try {
          const blob = new Blob(chunksRef.current, { type: mimeType });
          setVideoBlob(blob);

          // Simulate transcript for demo - replace with actual transcription service
          const mockTranscript = "This is a mock transcript. Replace with actual transcription service.";
          setAudioTranscript(mockTranscript);
          if (onTranscriptUpdate) {
            onTranscriptUpdate(mockTranscript);
          }
        } catch (err) {
          console.error('Error processing recording:', err);
          setError('Failed to process recording');
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start(1000); // Record in 1-second chunks
      setIsRecording(true);

    } catch (err) {
      console.error('Error starting recording:', err);
      setError(err instanceof Error ? err.message : 'Failed to start recording');
    }
  }, [isIOS, onTranscriptUpdate]);

  const cleanup = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setVideoBlob(null);
    setAudioTranscript('');
    setError(null);
  }, [stream]);

  return {
    startRecording,
    stopRecording,
    cleanup,
    isRecording,
    stream,
    audioTranscript,
    error,
    videoBlob
  };
};
